"use client";

import { CardsGrid as CardsGridUI } from "@repo/ui-parkmobile/CardsGrid";
import type { CardsGrid } from "@/generated/sb";
import {
  articleResolver,
  imageStoryblokResolver,
  buttonResolver,
  linkResolver,
  universalLinkResolver,
  sectionHeaderResolver,
  textResolver,
  titleResolver,
} from "@/lib/storyblok/adaptors";
import { useGlobalTranslations } from "@/hooks/useGlobalTranslations";
import { CardAlignableProps } from "@repo/ui-parkmobile/CardAlignable";
import { CardArticleProps } from "@repo/ui-parkmobile/CardArticle";
import { CardFeaturesProps } from "@repo/ui-parkmobile/CardFeatures";
import { CardPlansProps } from "@repo/ui-parkmobile/CardPlans";
import { CardStatsProps } from "@repo/ui-parkmobile/CardStats";
import { CardTestimonialProps } from "@repo/ui-parkmobile/CardTestimonial";
import { CardWithLinkProps } from "@repo/ui-parkmobile/CardWithLink";
import { storyblokEditable } from "@storyblok/react";
import {
  trackCardClick,
  trackArticleClick,
  trackStepInteraction,
  trackCtaSign,
} from "@/utils/trackingScripts";
import { extractTextFromRichtext } from "@/utils/analytics";
import type { TextHeader } from "@/generated/sb";
import { deleteAutoSlug } from "@/utils/autoSlug";

export function CardsGrid({
  blok,
}: {
  blok: CardsGrid & { lastTextHeader?: TextHeader };
}) {
  const {
    header,
    cards,
    layout,
    backgroundColor,
    topPadding,
    bottomPadding,
    imagesAspectRatio,
  } = blok;

  const translations = useGlobalTranslations(["readMore"]);

  const ownHeaderTitle = header?.[0]?.title?.[0]?.content;
  const lastTextHeaderTitle =
    blok.lastTextHeader?.content?.[0]?.title?.[0]?.content;
  const sectionName = ownHeaderTitle || lastTextHeaderTitle || "Cards Grid";

  const handleCardClick = (
    cardTitle?: string,
    contentDescription?: string,
    ctaText?: string,
    link?: string,
  ) => {
    trackCardClick({
      section: sectionName,
      config: "cardAlignable",
      cardTitle,
      contentDescription,
      ctaText,
      link,
    });
  };

  const resolvedCards = cards
    ?.map((card, index) => {
      switch (card.component) {
        case "cardStats": {
          if (!card) return null;
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          return {
            title: titleResolver(card.title),
            text: textResolver(card.text),
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio: imagesAspectRatio || "3/2",
                }
              : undefined,
            backgroundColor: card.backgroundColor || "none",
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardStats" as const,
            ...storyblokEditable(card),
          };
        }
        case "cardPlans": {
          if (!card) return null;
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          const linkBlok = card.link?.[0];
          const linkProps = linkBlok ? linkResolver([linkBlok]) : null;
          return {
            iconTitle: titleResolver(card.iconTitle),
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio:
                    imagesAspectRatio || iconAsset.aspectRatio || "3/2",
                }
              : undefined,
            title: titleResolver(card.title),
            subtitle: textResolver(card.subtitle),
            text: textResolver(card.text),
            link:
              linkProps && linkBlok
                ? {
                    text: linkBlok.text || "",
                    href: String(linkProps.href || ""),
                    target: linkProps.target,
                  }
                : null,
            backgroundColor: card.backgroundColor || "none",
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardPlans" as const,
            ...storyblokEditable(card),
          };
        }
        case "cardTestimonial": {
          if (!card) return null;
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          const formatDate = (dateString?: string): string | undefined => {
            if (!dateString) return undefined;
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return dateString;
              return date.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              });
            } catch {
              return dateString;
            }
          };
          return {
            name: titleResolver(card.name),
            date: formatDate(card.date),
            starsAmount: card.starsAmount ? parseInt(card.starsAmount, 10) : 5,
            text: textResolver(card.text),
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio:
                    imagesAspectRatio || iconAsset.aspectRatio || "3/2",
                }
              : undefined,
            backgroundColor: card.cardBackgroundColor || "primary",
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardTestimonial",
            ...storyblokEditable(card),
          };
        }
        case "cardAlignable": {
          if (!card) return null;
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          const links =
            card.link
              ?.map((linkBlok) => {
                const linkProps = linkResolver([linkBlok]);
                return linkProps
                  ? {
                      text: linkBlok.text || "",
                      href: String(linkProps.href || ""),
                      target: linkProps.target,
                    }
                  : null;
              })
              .filter(Boolean) || null;

          const rawTitle = card.title?.[0]?.content || "";
          const rawText = extractTextFromRichtext(card.text?.[0]?.content);

          const isStepCard = card.type === "step";
          const isAccountCard = (card as any).type === "account";

          let accountType: string | undefined;
          if (isAccountCard) {
            accountType = (card as any).accountType;

            if (!accountType && rawTitle) {
              if (
                rawTitle.toLowerCase().includes("business") ||
                rawTitle.toLowerCase().includes("fleet")
              ) {
                accountType = "Business Account";
              } else if (
                rawTitle.toLowerCase().includes("customer") ||
                rawTitle.toLowerCase().includes("personal")
              ) {
                accountType = "Customer Account";
              }
            }
          }

          const linkClickHandler =
            isAccountCard && accountType
              ? (linkText: string, linkIndex: number) => {
                  trackCtaSign({
                    section: sectionName,
                    config: "cardAlignable",
                    ctaText: linkText,
                    accountType,
                    contentDescription: rawText,
                  });
                }
              : undefined;

          const cardClickHandler = !isAccountCard
            ? (
                cardTitle?: string,
                contentDescription?: string,
                ctaText?: string,
              ) => {
                if (isStepCard) {
                  trackStepInteraction({
                    section: sectionName,
                    config: "cardAlignable",
                    stepNumber: index + 1,
                    stepTitle: rawTitle || cardTitle || "",
                    stepDescription: rawText || contentDescription || "",
                    interactionType: "click",
                  });
                } else {
                  const linkHref = links?.[0]?.href || "";
                  handleCardClick(
                    rawTitle || cardTitle,
                    rawText || contentDescription,
                    ctaText,
                    linkHref,
                  );
                }
              }
            : undefined;

          // const cardHoverHandler = isStepCard
          //   ? () => {
          //       trackStepInteraction({
          //         section: sectionName,
          //         config: "cardAlignable",
          //         stepNumber: index + 1,
          //         stepTitle: rawTitle,
          //         stepDescription: rawText,
          //         interactionType: "hover",
          //       });
          //     }
          //   : undefined;

          return {
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio: iconAsset.aspectRatio || "3/2",
                }
              : undefined,
            iconSize: card.iconSize || "small",
            title: titleResolver(card.title),
            text: textResolver(card.text),
            links: links && links.length > 0 ? links : null,
            backgroundColor: card.backgroundColor || "primary",
            aligning: card.aligning || "left",
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardAlignable",
            onCardClick: cardClickHandler,
            // onCardHover: cardHoverHandler,
            onLinkClick: linkClickHandler,
            ...storyblokEditable(card),
          };
        }
        case "cardFeatures": {
          if (!card) return null;
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          const features = card.features?.map((feature) => {
            const featureIconImage = feature.icon?.[0];
            const featureIconAsset = imageStoryblokResolver(featureIconImage);
            return {
              icon: featureIconAsset
                ? {
                    ...featureIconAsset,
                    aspectRatio:
                      imagesAspectRatio ||
                      featureIconAsset.aspectRatio ||
                      "3/2",
                  }
                : undefined,
              text: feature.text,
            };
          });
          const buttonBlok = card.button?.[0];
          const buttonProps = buttonBlok
            ? buttonResolver(buttonBlok) || undefined
            : undefined;
          return {
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio:
                    imagesAspectRatio || iconAsset.aspectRatio || "3/2",
                }
              : undefined,
            title: titleResolver(card.title) || undefined,
            text: textResolver(card.text) || undefined,
            features: features || [],
            button: buttonProps,
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardFeatures",
            ...storyblokEditable(card),
          };
        }
        case "cardArticle": {
          if (!card) return null;

          const resolvedArticle =
            card.article && typeof card.article === "object"
              ? card.article
              : null;

          const linkBlok = card.link?.[0];
          const linkProps = linkBlok ? linkResolver([linkBlok]) : null;

          const articleLink = resolvedArticle
            ? {
                text: translations.readMore || "Read More",
                href: `/${deleteAutoSlug(resolvedArticle.full_slug)}`,
                target: undefined,
              }
            : undefined;

          const articleData = resolvedArticle
            ? articleResolver({
                article: resolvedArticle,
                imageAspectRatio: imagesAspectRatio || "3/2",
                disableDescription: card.disableDescription,
              })
            : null;
          if (!articleData) return null;

          const cardTitle = resolvedArticle
            ? articleData.title
            : titleResolver(card.title)?.content;
          const cardDate = resolvedArticle?.created_at || card.date;
          const cardCtaText =
            articleLink?.text || (linkBlok?.text ? linkBlok.text : "Read More");

          return {
            layout: card.layout || "vertical",
            backgroundColor: card.backgroundColor || "primary",
            image: articleData.image,
            title: resolvedArticle
              ? {
                  content: articleData.title,
                  level: "h6" as const,
                  fontSize: "p" as const,
                  className: card.disableDescription
                    ? "uipm:font-semibold"
                    : undefined,
                }
              : titleResolver(card.title) || undefined,
            text: articleData.description,
            link:
              articleLink ||
              (linkProps && linkBlok
                ? {
                    text: linkBlok.text || "",
                    href: String(linkProps.href || ""),
                    target: linkProps.target,
                  }
                : undefined),
            date:
              resolvedArticle?.content?.publicationDate ||
              resolvedArticle?.created_at ||
              card.date,
            onCardClick: (title?: string, date?: string, ctaText?: string) => {
              const linkHref =
                articleLink?.href ||
                (linkProps && linkBlok ? String(linkProps.href || "") : "");
              trackArticleClick({
                section: sectionName,
                config: "cardArticle",
                articleTitle: title || cardTitle || "",
                articleDate: date || "",
                ctaText: ctaText || cardCtaText,
                link: linkHref,
              });
            },
            cardType: "cardArticle",
            ...storyblokEditable(card),
          };
        }
        case "cardWithLink": {
          if (!card) return null;
          const link = universalLinkResolver(card.link);
          const iconImage = card.icon?.[0];
          const iconAsset = imageStoryblokResolver(iconImage);
          return {
            title: titleResolver(card.title),
            text: textResolver(card.text),
            icon: iconAsset
              ? {
                  ...iconAsset,
                  aspectRatio:
                    imagesAspectRatio || iconAsset.aspectRatio || "3/2",
                }
              : undefined,
            href: link?.href ? String(link.href) : undefined,
            target: link?.target,
            backgroundColor: card.backgroundColor || "none",
            layout: card.layout || "wide",
            verticalPadding: card.verticalPadding || "default",
            horizontalPadding: card.horizontalPadding || "default",
            cardType: "cardWithLink",
            ...storyblokEditable(card),
          };
        }
        default:
          return null;
      }
    })
    .filter(Boolean) as (
    | (CardWithLinkProps & { cardType: "cardWithLink" })
    | (CardStatsProps & { cardType: "cardStats" })
    | (CardPlansProps & { cardType: "cardPlans" })
    | (CardTestimonialProps & { cardType: "cardTestimonial" })
    | (CardAlignableProps & { cardType: "cardAlignable" })
    | (CardFeaturesProps & { cardType: "cardFeatures" })
    | (CardArticleProps & { cardType: "cardArticle" })
  )[];

  return (
    <CardsGridUI
      {...storyblokEditable(blok)}
      header={sectionHeaderResolver(header)}
      cards={resolvedCards}
      layout={layout}
      backgroundColor={backgroundColor}
      topPadding={topPadding}
      bottomPadding={bottomPadding}
      imagesAspectRatio={imagesAspectRatio}
      id={blok.anchor}
    />
  );
}
